import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getOrderStatus, type OrderStatusResponse } from "../lib/api";

const POLL_INTERVAL_MS = 4000;
const TERMINAL_STATUSES = new Set(["complete", "failed"]);

const STATUS_COPY: Record<
  OrderStatusResponse["status"],
  { label: string; blurb: string }
> = {
  pending: {
    label: "Payment pending",
    blurb: "Waiting for your payment to be confirmed.",
  },
  paid: {
    label: "Payment received",
    blurb: "We're getting your campaign ready to build.",
  },
  queued: { label: "Queued", blurb: "Your campaign is in line to be generated." },
  generating: {
    label: "Generating",
    blurb: "We're building your ReleaseMachine campaign now.",
  },
  preparing_package: {
    label: "Preparing package",
    blurb: "Finalizing your files for delivery.",
  },
  complete: {
    label: "Your campaign is ready",
    blurb: "View it below, or check your email for the link.",
  },
  failed: {
    label: "Generation failed",
    blurb: "Something went wrong building your campaign.",
  },
};

export function OrderStatus() {
  const { token = "" } = useParams<{ token: string }>();
  const [status, setStatus] = useState<OrderStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const result = await getOrderStatus(token);
        if (cancelled) return;
        setStatus(result);
        setError(null);
        if (!TERMINAL_STATUSES.has(result.status)) {
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        setError("We couldn't find that order. Double-check your link.");
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token]);

  if (error) {
    return (
      <div className="status-shell">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="status-shell">
        <span className="spinner" role="status" aria-label="Loading" />
      </div>
    );
  }

  const copy = STATUS_COPY[status.status];
  const badgeClass =
    status.status === "complete"
      ? "is-complete"
      : status.status === "failed"
        ? "is-failed"
        : "";

  return (
    <div className="status-shell">
      <span className={`status-badge ${badgeClass}`}>{copy.label}</span>
      <h1 style={{ fontSize: "1.6rem", marginBottom: 12 }}>
        {status.artistName} — {status.songTitle}
      </h1>
      <p style={{ color: "var(--text-muted)" }}>{copy.blurb}</p>

      {!TERMINAL_STATUSES.has(status.status) ? (
        <p style={{ marginTop: 20, color: "var(--text-faint)", fontSize: "0.85rem" }}>
          <span className="spinner" style={{ marginRight: 8, verticalAlign: "middle" }} />
          This page updates automatically — you don&rsquo;t need to keep it open.
          We&rsquo;ll email you when it&rsquo;s ready.
        </p>
      ) : null}

      {status.status === "failed" ? (
        <div className="alert alert-error" style={{ marginTop: 24, textAlign: "left" }}>
          {status.errorMessage ?? "Please contact support and reference this order link."}
        </div>
      ) : null}

      {status.status === "complete" && status.downloadToken ? (
        <div className="status-actions">
          <a
            className="btn btn-primary"
            href={`/download/${status.downloadToken}/campaign.html`}
            target="_blank"
            rel="noreferrer"
          >
            View Campaign
          </a>
          <a
            className="btn btn-secondary"
            href={`/download/${status.downloadToken}/campaign.html?dl=1`}
          >
            Download Campaign
          </a>
          <a
            className="btn btn-secondary"
            href={`/download/${status.downloadToken}/campaign.md`}
          >
            Download Markdown
          </a>
          <a
            className="btn btn-secondary"
            href={`/download/${status.downloadToken}/campaign.txt`}
          >
            Download Plain Text
          </a>
        </div>
      ) : null}
    </div>
  );
}
